<template>
  <el-drawer
    v-model="visible"
    :modal="false"
    :append-to-body="false"
    :fullscreen="false"
    :size="drawerWidthPct"
    destroy-on-close
    class="conversation-detail-drawer"
    modal-class="conversation-detail-drawer"
    @opened="onOpened"
    @closed="onClosed"
  >
    <template #header>
      <div v-if="isDataLoaded" class="conversation-detail-drawer__header">
        <template v-if="isAccessible">
          <template v-if="!$screen.isMobile($mq)">
            <avatar :person="details.assignee" :size="24" />
            <priority-chicklet :priority="details.priority" size="medium" />
            <status-chicklet :status="details.status" size="medium" />
            <due-date-chicklet :due-date="details.dueDate" size="medium" />
          </template>
          <linked-item-drawer-dropdown
            v-if="originatingTask"
            :label="unlinkLabel"
            @unlink-item-click="$emit('unlink-conversation')"
          />
        </template>
        <prev-next-arrows
          v-if="prevItemForNav || nextItemForNav"
          :previous-item="prevItemForNav"
          :next-item="nextItemForNav"
          @navigate="(item) => $emit('navigate', item)"
        />
      </div>
    </template>
    <template v-if="isDataLoaded">
      <template v-if="isAccessible">
        <div class="conversation-detail-drawer__breadcrumb">
          <span>{{details.mailbox.name}}</span>
          &nbsp;>&nbsp;
          <span>#{{details.refNumber}}</span>
        </div>
        <!-- SPAM HEADER -->
        <spam-header
          :spam-type="spamType"
          class="conversation-detail-drawer__spam-header"
        />
        <div class="conversation-detail-drawer__subject">{{details.subject}}</div>
        <!--MOBILE META DATA -->
        <div v-if="$screen.isMobile($mq)" class="conversation-detail-drawer__meta-mobile">
          <avatar :person="details.assignee" :size="24" />
          <priority-chicklet :priority="details.priority" size="medium" />
          <status-chicklet :status="details.status" size="medium" max-width="130px" />
          <due-date-chicklet :due-date="details.dueDate" size="medium" />
        </div>
        <div class="conversation-detail-drawer__message">
          <div class="user-inputed-rich-text" v-html="details.message" />
        </div>
        <div class="conversation-detail-drawer__history">
          <div
            v-for="(entry, index) in history"
            :key="index"
            :class="{'conversation-detail-drawer__history-entry-reply' : index !== 0 && entry.isConvoThread }"
            class="conversation-detail-drawer__history-entry"
          >
            <div class="conversation-detail-drawer__history-entry__action">
              <span v-html="entry.createdOn" />
              <span v-html="entry.action" />
            </div>
            <div
              v-if="index !== 0 && entry.description"
              class="conversation-detail-drawer__history-entry__description user-inputed-rich-text"
              v-html="entry.description"
            />
            <!-- ATTACHMENTS LIST -->
            <attachments-list :attachments="entry.attachments" />
          </div>
        </div>
        <div class="conversation-detail-drawer__footer">
          <dd-button
            :color="ColorAssistant.MailboxColor"
            full-width
            size="large" 
            @click.exact="goToConversation()"  
            @click.meta="goToConversation(true)"
          >View full conversation</dd-button>
        </div>
      </template>
      <!-- No access -->
      <div v-else class="conversation-detail-drawer__no-access">
        <h2 v-if="requestedAccessToMailbox">Access requested!</h2>
        <h2 v-else>Request access to this conversation</h2>
        <div class="conversation-detail-drawer__breadcrumb">
          <span>{{conversation.entity.name}}</span>
          &nbsp;>&nbsp;
          <span>#{{conversation.refNumber}}</span>
        </div>
        <p v-if="requestedAccessToMailbox">{{ mailboxRequestMessage }}</p>
        <template v-else>
          <p>
            You don&rsquo;t have access to this conversation&rsquo;s mailbox:
            <strong>{{conversation.entity.name}}</strong>.
            Click the button below and we&rsquo;ll send an email to a mailbox administrator.
            Once approved, you can view this conversation.
          </p>
          <dd-button
            :is-loading="isRequestingAccess"
            color="brownish-orange"
            size="large"
            @click="requestAccess()"
          >Request Access +</dd-button>
        </template>
      </div>
    </template>
    <loading-placeholder v-else :lines="20" />
  </el-drawer>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import { uniq } from 'lodash-es'

/* Mixins */

/* Components */
import SpamHeader from '@/components/conversation/spam-header.vue'

/* Helpers */
import TextAssistant from '@/helpers/text-assistant'
import RouteAssistant from '@/helpers/route-assistant'
import TypeAssistant from '@/helpers/type-assistant'
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { ILabelValuePair } from '@/types/data-objects/global'
import { IBreadcrumbItemMenuItem } from '@/types/data-objects/items'
import { IAttachment } from '@/types/data-objects/attachments'
import {
  IConvosOutputDetail,
  IConvosOutputHistoryv12,
  IItemReference,
  ILinkedItemForListing,
} from '@/api/types/response.types'
import { EEntities } from '@/types'

/* API */
import ConversationsAPI from '@/api/modules/convos.api'
import MailboxesAPI from '@/api/modules/mailboxes.api'

export default defineComponent({
  name: 'ConversationDetailDrawer',

  components: {
    SpamHeader,
  },

  props: {
    showDrawer: {
      type: Boolean,
      default: false,
    },
    /* If hydrated, allows unlinking from task */
    originatingTask: {
      type: Object as () => IItemReference,
      default: null,
    },
    conversation: {
      type: Object as () => ILinkedItemForListing,
      required: true,
    },
    prevItem: {
      type: Object as () => ILinkedItemForListing,
      required: false,
      default: null,
    },
    nextItem: {
      type: Object as () => ILinkedItemForListing,
      required: false,
      default: null,
    },
  },

  emits: ['navigate', 'unlink-conversation', 'update:showDrawer'],

  data() {
    return {
      ColorAssistant,
      drawerOpened: false,
      isDataLoaded: false,
      isAccessible: false,
      isRequestingAccess: false,
      mailboxesRequestedAccessTo: [] as ILabelValuePair[],
      details: {} as IConvosOutputDetail,
      globalAttachments: [] as IAttachment[],
      history: [] as IConvosOutputHistoryv12[],
    }
  },

  computed: {
    visible: {
      get(): boolean {
        return this.showDrawer
      },
      set(newValue: boolean) {
        this.$emit('update:showDrawer', newValue)
      },
    },
    drawerWidthPct(): string {
      if (this.$screen.isMobile(this.$mq)) {
        return '100%'
      }
      return '524px'
    },
    prevItemForNav(): IBreadcrumbItemMenuItem {
      if (!this.prevItem) {
        return null
      }
      return {
        itemID: this.prevItem.id,
        entityID: this.prevItem.entity.id,
        entityName: this.prevItem.entity.name,
        entityType: TextAssistant.entitySingularName(EEntities.MAILBOXES),
        refNumber: this.prevItem.refNumber,
        title: this.prevItem.title,
      }
    },
    nextItemForNav(): IBreadcrumbItemMenuItem {
      if (!this.nextItem) {
        return null
      }
      return {
        itemID: this.nextItem.id,
        entityID: this.nextItem.entity.id,
        entityName: this.nextItem.entity.name,
        entityType: TextAssistant.entitySingularName(EEntities.MAILBOXES),
        refNumber: this.nextItem.refNumber,
        title: this.nextItem.title,
      }
    },
    requestedAccessToMailbox(): boolean {
      return this.mailboxesRequestedAccessTo.some(p => {
        return p.value === this.conversation.entity.id
      })
    },
    mailboxRequestMessage(): string {
      return this.mailboxesRequestedAccessTo.find(p => {
        return p.value === this.conversation.entity.id
      }).label
    },
    unlinkLabel(): string {
      return 'Unlink from #' + this.originatingTask.refNumber
    },
    spamType(): number {
      return TypeAssistant.getSpamType(this.details.spamStatus)
    },
    isSpam(): boolean {
      return TypeAssistant.isSpam(this.spamType)
    },
  },

  watch: {
    conversation: {
      /* Subsequent data fetches (prev/next arrows). Check for drawerOpened to avoid
         double fetching on initial conversation load */
      async handler(value) {
        if (this.drawerOpened) {
          await this.fetchData()
        }
      },
    },
    drawerOpened: {
      /* Initial data fetch should happen only after drawer finishes opening */
      async handler(value) {
        if (value) {
          await this.fetchData()
        }
      },
    },
  },

  methods: {
    async onOpened() {
      this.drawerOpened = true
    },

    onClosed() {
      this.drawerOpened = false
      this.isDataLoaded = false
      this.clearData()
    },

    clearData() {
      this.details = {} as IConvosOutputDetail
      this.globalAttachments = [] as IAttachment[]
      this.history = [] as IConvosOutputHistoryv12[]
    },

    async fetchData() {
      this.isDataLoaded = false
      try {
        await Promise.all([this.fetchHistory(), this.fetchConversationData()])
        this.isAccessible = true
      } catch (error) {
        if (error.status === 403) {
          this.isAccessible = false
        } else {
          this.$error.emit(error, true)
        }
      } finally {
        this.isDataLoaded = true
      }
    },

    async fetchHistory() {
      const response = await ConversationsAPI.get_History_v1_2(
        this.conversation.entity.id,
        this.conversation.id,
        'chron',
      )

      const allAttachments: IAttachment[] = []
      response.forEach(item => {
        allAttachments.push(...item.attachments)
      })
      this.globalAttachments = uniq(allAttachments)
      this.history = response
    },

    async fetchConversationData() {
      this.details = await ConversationsAPI.get_Detail(
        this.conversation.entity.id,
        this.conversation.id,
      )
    },

    async requestAccess() {
      try {
        this.isRequestingAccess = true
        const response = await MailboxesAPI.put_RequestAccess(
          this.conversation.entity.id,
        )
        const mailboxAccessRequested = {
          label: response.message,
          value: this.conversation.entity.id,
        }
        this.mailboxesRequestedAccessTo.push(mailboxAccessRequested)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isRequestingAccess = false
      }
    },

    goToConversation(newWindow?: boolean) {
      RouteAssistant.goToConversation(
        this.$router,
        this.$route.params.accountId,
        this.conversation.entity.id,
        this.conversation.id,
        this.conversation.entity.name,
        this.conversation.refNumber,
        this.conversation.title,
        newWindow,
      )
    },
  },
})
</script>

<style lang="scss">
.conversation-detail-drawer {
  &__header {
    display: flex;
    align-items: center;

    > div {
      margin-right: 6px;
    }

    > div.lidd {
      margin-left: 4px;
    }

    > div:last-child {
      margin-left: auto;
    }
  }

  &__spam-header {
    margin-bottom: 20px;
    border-radius: 8px;
  }

  &__meta-mobile {
    display: flex;
    align-items: center;

    > div {
      margin-right: 8px;
    }

    > .avatar {
      margin-left: 0;
    }

    padding-bottom: 24px;
  }

  &__breadcrumb {
    padding-bottom: 16px;
    @include lettering--p-sm;
    color: var(--color-text-third);
  }

  &__subject {
    padding-bottom: 20px;
    @include lettering--h2;
  }

  &__message {
    margin-bottom: $detail-description-bottom-buffer;
  }

  &__history {
    padding-top: 10px;
    height: 100%;
    width: calc(100% + 60px);
    left: -30px;
    position: relative;
    background-color: var(--color-background-off-white);
    border-top: $border--solid-light;

    &-entry {
      padding: 20px 30px;
      background-color: var(--color-background-off-white);

      &:not(:first-child) {
        border-top: $border--dotted-light;
      }

      &:last-child {
        padding-bottom: 100px;
      }

      &__action {
        display: flex;
        flex-direction: column;
        padding-bottom: 20px;

        /* Created on */
        > span:first-child {
          padding-bottom: 2px;
          font-size: 12px;
          color: $slate;
        }

        /* Action text */
        > span:last-child > p {
          line-height: 20px;
        }
      }

      &__description {
        margin-bottom: $detail-description-bottom-buffer;
      }

      &-reply {
        background-color: var(--color-general-white);
      }
    }
  }

  &__footer {
    display: flex;
    justify-content: center;
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: 20px 30px;
    background-color: var(--color-general-white);
    border-top: solid 1px $silver-two;
  }

  &__no-access {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    > button {
      margin-top: 20px;
      width: 100%;
    }
  }
}
</style>
